import React from "react";
import styled from "styled-components";
import moment from "moment";
import { GET_LIST, Title } from "react-admin";
import { LinearProgress, Paper, Select, MenuItem, Card, CardContent, Typography, Grid, Toolbar, InputLabel, FormControl } from "@material-ui/core";
import dataProviderFactory from "../dataProvider";
import { pluck, values, unnest, reduce, mergeWith, add, propEq, isNil, filter, map, range, reverse, addIndex, isEmpty, prop, find, forEach, equals, propOr, groupBy } from "ramda";
import { BarChart, Bar, XAxis, YAxis, LabelList, ResponsiveContainer, Tooltip } from 'recharts';

const StyledFormControl = styled(FormControl)`
  min-width: 135px !important;
  margin-right: 1em !important;
`;

const LineBar = (props) => {
  const { fill, x, y, width, height, value } = props;

  if (value <= 0) {
    return <svg></svg>;
  }

  return <svg>
    <line x1={x + width} x2={x + width} y1={y + height} y2={y} stroke={fill} strokeWidth={2}/>
  </svg>;
};

const CustomizedTick = (props) => {
  return (
    <svg>
      <g>
        <rect x={props.x - props.width} y={props.y - props.payload.offset} height={props.payload.offset * 2} width={props.width + 8} fill={props.color} stroke="black"></rect>
        <text x={props.x} y={props.y} textAnchor={props.textAnchor} alignmentBaseline={props.verticalAnchor}>{props.payload.value}</text>
      </g>
    </svg>
  );
};

class FleetAllocation extends React.Component {
  state = {
    reloading: true,
    locations: [],
    models: [],
    currentLocation: 0,
    currentModel: 0,
    currentYear: new Date().getFullYear(),
    fleet: {},
    graphDataByLocation: {},
    graphDomain: [0, 'auto'],
  }

  async fetchData() {
    const { currentYear } = this.state;
    const dataProvider = await dataProviderFactory("rest");
    const getFleetAllocation = await dataProvider(GET_LIST, "fleet_allocation", {
      filter: {
        year: currentYear,
      },
      pagination: {},
      sort: {},
    });

    this.setState({ fleet: getFleetAllocation.data });
  }

  async fetchLocations() {
    const dataProvider = await dataProviderFactory("rest");
    const locations = await dataProvider(GET_LIST, "locations", {
      filter: { only_bases: "true" },
      pagination: {
        page: 1,
        perPage: 999
      },
      sort: { field: 'id', order: "DESC" }
    });

    const baseLocations = filter(location => {
      return isNil(location.base_location_id) && !propEq("location_name", "Other", location);
    }, locations.data);

    this.setState({ locations: baseLocations });
  }

  async fetchVehicleModels() {
    const dataProvider = await dataProviderFactory("rest");
    const vehicleModels = await dataProvider(GET_LIST, "vehicle_models", {
      filter: { active: true, type: "2_3" },
      pagination: {
        page: 1,
        perPage: 99999
      },
      sort: { field: "order", order: "ASC" }
    });

    this.setState({ models: vehicleModels.data });
  }

  componentDidMount() {
    this.fetchData();
    this.fetchLocations();
    this.fetchVehicleModels();
  }

  componentDidUpdate(prevProps, prevState) {
    const { fleet: prevFleet, currentModel: prevCurrentModel } = prevState;
    const { fleet, locations, models, reloading, currentModel } = this.state;

    if (!reloading && (isEmpty(fleet) || isEmpty(locations) || isEmpty(models))) {
      this.setState({ reloading: true })
    }

    if (reloading && !isEmpty(fleet) && !isEmpty(locations) && !isEmpty(models)) {
      this.setState({ reloading: false })
    }

    if (!equals(fleet, prevFleet)) {
      this.updateGraphData();
    }

    if (currentModel !== prevCurrentModel) {
      this.updateGraphData();
    }

  }

  handleChange = event => {
    if (event.target.name === 'currentYear') {
      this.setState({ 
        currentYear: event.target.value,
        reloading: true,
        fleet: {}, 
      }, () => this.fetchData());
    } else {
      this.setState({ [event.target.name]: event.target.value });
    }
  };

  updateGraphData() {
    this.setState((state) => {
      let newGraphData = {};
      const locations = state.locations;
      const fleet = state.fleet;
      const currentModel = state.currentModel;
      const mapIndexed = addIndex(map);
      const months = moment.months();
      let maxVehicles = 0;

      if (!isEmpty(locations) && !isEmpty(fleet)) {
        const modelData = currentModel > 0 ? prop(currentModel, fleet) : unnest(values(fleet));

        forEach(location => {
          const dataForLocation = reduce(mergeWith(add), {}, pluck(location.id , modelData))
          const recommendedBikes = propOr([], 'recommended_bikes', location);
          let recommendedBikesForModel;

          if (currentModel > 0) {
            recommendedBikesForModel = filter(propEq('vehicle_model_id', currentModel), recommendedBikes);
          } else {
            recommendedBikesForModel = recommendedBikes;
          }

          const recommendedBikesPerMonth = groupBy(prop('month_index'), recommendedBikesForModel)
          
          newGraphData[location.id] = mapIndexed( (month, index) => {
            const numberOfVehicles = Number(dataForLocation[index].toFixed(1));
            let recommendedBikeNumbers = reduce(mergeWith(add), {}, propOr([], index, recommendedBikesPerMonth));
            
            recommendedBikeNumbers = isEmpty(recommendedBikeNumbers) || isNil(recommendedBikeNumbers) ? {upper: 0, lower: 0} : recommendedBikeNumbers;
            maxVehicles = numberOfVehicles > maxVehicles ? numberOfVehicles : maxVehicles;

            return { name: month, value: numberOfVehicles, upper: recommendedBikeNumbers.upper, lower: recommendedBikeNumbers.lower};
          }, months)

        }, locations);
      }
      
      return {
        graphDataByLocation: newGraphData,
        graphDomain: maxVehicles > 0 ? [0, maxVehicles + 10] : [0, 'auto'],
      };
    });
  }

  buildLocationsMenuItems(locations) {
    return map(location => {
      return (
        <MenuItem key={location.id} value={location.id}>
          {location.location_name}
        </MenuItem>
      );
    }, locations);
  }

  buildModelsMenuItems(models) {
    return map(model => {
      return (
        <MenuItem key={model.id} value={model.id}>
          {model.model_name}
        </MenuItem>
      );
    }, models);
  }

  buildFleetInformation(locations, currentLocation, graphDataByLocation, graphDomain) {
    if (currentLocation > 0) {
      const location = find(propEq('id', currentLocation))(locations)
      return this.buildLocationFleetCard(location, prop(location.id, graphDataByLocation), graphDomain)
    } else {
      return map(location => this.buildLocationFleetCard(location, prop(location.id, graphDataByLocation), graphDomain), locations);
    }
  }

  buildLocationFleetCard(location, data, graphDomain) {
    return (
      <Grid item key={location.id} xs={12}>
        <Card>
        <CardContent>
          <Typography variant="subheading" color="primary">{location.location_name}</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              layout="vertical"
              data={data}
              barGap={-15}
              barSize={15}
              margin={{ top: 15, right: 30, left: 20, bottom: 5 }}
            >
              <Tooltip />
              <XAxis type="number" domain={graphDomain} hide />
              <YAxis dataKey="name" type="category" width={90} tick={<CustomizedTick color={location.color} />} />           
              <Bar fill="#CCCCCC" dataKey="value" background={{ fill: '#f2f2f2' }} stroke="black">
                <LabelList dataKey="value" position="right" />
              </Bar>
              <Bar fill="#008000" shape={<LineBar/>} dataKey="upper" ></Bar>
              <Bar fill="#FF0000" shape={<LineBar/>} dataKey="lower" ></Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      </Grid>
      
    );
  }

  buildYearsMenuItems() {
    const yearRange = range(new Date().getFullYear() - 10, new Date().getFullYear() + 2);
    const mapIndexed = addIndex(map);

    return mapIndexed( (year, index) => {
      return (
        <MenuItem key={index} value={year}>
          {year}
        </MenuItem>
      );
    }, reverse(yearRange));
  }

  render() {
    const { locations, currentLocation, models, currentModel, currentYear, reloading, graphDataByLocation, graphDomain } = this.state;

    return (
      <React.Fragment>
        <Title title="Fleet Allocation" />
        <Grid container spacing={24}>
          <Grid item xs={12}>
            <Paper elevation={1}>
              {(this.state === null || (this.state !== null && reloading)) && <LinearProgress />}
              <Toolbar>
              <StyledFormControl>
              <InputLabel shrink id="demo-simple-select-placeholder-label-label">
                  Year
                </InputLabel>
              <Select
                value={currentYear}
                onChange={this.handleChange}
                inputProps={{
                  name: "currentYear",
                  id: "year"
                }}
              >
                {this.buildYearsMenuItems()}
              </Select>
              </StyledFormControl>
              
              <StyledFormControl>
              <InputLabel shrink id="demo-simple-select-placeholder-label-label">
                  Location
                </InputLabel>
              <Select
                value={currentLocation}
                onChange={this.handleChange}
                inputProps={{
                  name: "currentLocation",
                  id: "location"
                }}
              >
                <MenuItem key={0} value={0}>
                  All Cities
                </MenuItem>
                {this.buildLocationsMenuItems(locations)}
              </Select>
              </StyledFormControl>
              <StyledFormControl>
              <InputLabel shrink id="demo-simple-select-placeholder-label-label">
                  Vehicle Model
                </InputLabel>
              <Select
                value={currentModel}
                onChange={this.handleChange}
                inputProps={{
                  name: "currentModel",
                  id: "model"
                }}
              >
                <MenuItem key={0} value={0}>
                  All Models
                </MenuItem>
                {this.buildModelsMenuItems(models)}
              </Select>
              </StyledFormControl>
              
            </Toolbar>
            </Paper>
          
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={24}>
              {!reloading && this.buildFleetInformation(locations, currentLocation, graphDataByLocation, graphDomain)}
            </Grid>
          </Grid>
          
          
        </Grid>
      </React.Fragment>
    );
  }
}

export default FleetAllocation;



// WEBPACK FOOTER //
// ./src/components/fleetAllocation.js